
import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils/cn";
import { formatCount } from "@/lib/utils/format";
import { useToast } from "@/components/ui/ToastProvider";
import { IconComment, IconShare, IconBookmark, IconBookmarkFilled, IconMoreHorizontal } from "@/components/ui/Icons";
import type { Post } from "@/types";

interface PostActionsProps {
  post: Post;
  userId: string | null;
  localBookmarked: boolean;
  onBookmarkChange: (bookmarked: boolean) => void;
  postUrl: string;
}

export function PostActions({
  post,
  userId,
  localBookmarked,
  onBookmarkChange,
  postUrl,
}: PostActionsProps) {
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [isBookmarking, setIsBookmarking] = useState(false);
  const { success, info, error: showError } = useToast();

  const handleBookmark = useCallback(async () => {
    if (!userId) {
      info("Sign in to bookmark", "Save posts to your reading list.");
      return;
    }

    if (isBookmarking) return;

    const prev = localBookmarked;
    onBookmarkChange(!prev);
    setIsBookmarking(true);

    try {
      const res = await fetch(`/api/posts/${post.id}/bookmark`, {
        method: prev ? "DELETE" : "POST",
      });

      if (!res.ok) {
        onBookmarkChange(prev);
        throw new Error("Bookmark failed");
      }

      if (!prev) {
        success("Post bookmarked", "Saved to your bookmarks.");
      }
    } catch {
      showError("Action failed", "Please try again.");
    } finally {
      setIsBookmarking(false);
    }
  }, [userId, post.id, localBookmarked, onBookmarkChange, isBookmarking, success, info, showError]);

  const handleShare = useCallback(async () => {
    const url = `${window.location.origin}${postUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          url,
        });
      } catch {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      success("Link copied", "Post link copied to clipboard.");
    }
    setShareMenuOpen(false);
  }, [post.title, postUrl, success]);

  return (
    <div className="flex items-center gap-0.5 -mx-1 mt-2">
      {/* Comments */}
      <Link to={postUrl}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-text-subtle hover:text-foreground hover:bg-muted transition-colors duration-150 text-xs font-medium"
      >
        <IconComment className="w-4 h-4" />
        <span>{formatCount(post.comment_count)}</span>
        <span className="hidden sm:inline text-text-subtle ml-0.5">
          {post.comment_count === 1 ? "comment" : "comments"}
        </span>
      </Link>

      {/* Share */}
      <button
        onClick={handleShare}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-text-subtle hover:text-foreground hover:bg-muted transition-colors duration-150 text-xs font-medium"
      >
        <IconShare className="w-4 h-4" />
        <span className="hidden sm:inline">Share</span>
      </button>

      {/* Bookmark */}
      <motion.button
        onClick={handleBookmark}
        whileTap={{ scale: 0.9 }}
        className={cn(
          "flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl transition-colors duration-150 text-xs font-medium",
          localBookmarked
            ? "text-brand hover:bg-brand-muted"
            : "text-text-subtle hover:text-foreground hover:bg-muted"
        )}
        aria-pressed={localBookmarked}
        aria-label={localBookmarked ? "Remove bookmark" : "Bookmark post"}
      >
        {localBookmarked ? (
          <IconBookmarkFilled className="w-4 h-4" />
        ) : (
          <IconBookmark className="w-4 h-4" />
        )}
        <span className="hidden sm:inline">
          {localBookmarked ? "Saved" : "Save"}
        </span>
      </motion.button>

      {/* View count */}
      <span className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-text-subtle ml-auto">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        <span>{formatCount(post.view_count)}</span>
      </span>
    </div>
  );
}
